import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import promo1 from "../../static/media/tienda/Descuento_1.webp";
import promo2 from "../../static/media/tienda/Descuento_2.webp";
import promo3 from "../../static/media/tienda/Descuento_3.webp";
import promo4 from "../../static/media/tienda/Descuento_4.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  gold: "#FFCD00",
  white: "#ffffff",
};

const PROMOS = [
  { id: 1, img: promo1 },
  { id: 2, img: promo2 },
  { id: 3, img: promo3 },
  { id: 4, img: promo4 },
];

// Agrupamos en pares para la vista móvil (1 columna, 2 filas por slide)
const MOBILE_PROMOS = [
  [PROMOS[0], PROMOS[1]],
  [PROMOS[2], PROMOS[3]],
];

export default function Promociones() {
  return (
    <Box
      id="productos"
      sx={{
        background: `radial-gradient(circle at 50% 35%, #006cd4 0%, ${C.navy} 60%, #06131e 100%)`,
        minHeight: { xs: "auto", md: "100vh" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        pt: { xs: "120px", md: "150px" },
        pb: { xs: "25vw", md: "12vw" },
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
          maskImage:
            "radial-gradient(circle at 50% 50%, black 30%, transparent 80%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "20px", sm: "40px", md: "56px" },
          width: "100%",
          zIndex: 1,
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        {/* TÍTULO PERFECTAMENTE CENTRADO CON LÍNEA */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            mb: { xs: 6, md: 10 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: C.white,
              fontWeight: 750,
              fontSize: { xs: 28, sm: 34, md: 40 },
              lineHeight: 1.15,
              letterSpacing: "-.02em",
              mb: 2,
              width: "100%",
            }}
          >
            Nuestros Productos
          </Typography>
          <Box
            sx={{
              width: "60px",
              height: "3px",
              background: C.gold,
              borderRadius: "2px",
            }}
          />
        </Box>

        {/* --- VISTA DESKTOP: GRID 2x2 --- */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 4,
            width: "100%",
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          {PROMOS.map((promo, index) => (
            <Box
              key={promo.id}
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              sx={{
                width: "100%",
                aspectRatio: "1/1",
                borderRadius: "24px",
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                background: C.white,
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer",
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
                },
              }}
            >
              <Box
                component="img"
                src={promo.img}
                alt={`Promoción ${promo.id}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>

        {/* --- VISTA MOBILE: SWIPER (1 Columna, 2 Filas por Slide) --- */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            width: "100%",
            pb: 2,
          }}
        >
          <Swiper
            modules={[Autoplay]}
            loop={true}
            slidesPerView={1}
            spaceBetween={20}
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            style={{ width: "100%" }}
          >
            {MOBILE_PROMOS.map((pair, index) => (
              <SwiperSlide key={index}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center", // Centra las dos tarjetas en la columna
                    justifyContent: "center",
                    gap: 3,
                    width: "100%",
                    px: 1,
                    boxSizing: "border-box",
                  }}
                >
                  {pair.map((promo) => (
                    <Box
                      key={promo.id}
                      sx={{
                        width: "100%",
                        maxWidth: "320px", // Limita el ancho para que no se desborde ni se corte
                        aspectRatio: "1/1",
                        borderRadius: "24px",
                        background: C.white,
                        boxShadow: "0 15px 35px rgba(0,0,0,0.3)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                        boxSizing: "border-box", // Previene que el padding dañe el tamaño real
                      }}
                    >
                      <Box
                        component="img"
                        src={promo.img}
                        alt={`Promoción ${promo.id}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain", // Garantiza que se vea completa
                          display: "block",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>

      {/* CURVA SINUOSA INFERIOR */}
      <Box
        sx={{
          position: "absolute",
          bottom: -1,
          left: 0,
          width: "100%",
          overflow: "hidden",
          lineHeight: 0,
          zIndex: 10,
          pointerEvents: "none",
        }}
      >
        <svg
          viewBox="0 0 1440 320"
          style={{
            display: "block",
            width: "100%",
            height: "12vw",
            minHeight: "60px",
          }}
          preserveAspectRatio="none"
        >
          <path
            fill={C.white}
            d="M0,224L80,213.3C160,203,320,181,480,186.7C640,192,800,224,960,218.7C1120,213,1280,171,1360,149.3L1440,128L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
          ></path>
        </svg>
      </Box>
    </Box>
  );
}
